import { API } from '../api.js';
import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LangContext';
import Paginator from '../components/Paginator';
import ConfirmDialog from '../components/ConfirmDialog';
import useBackButton from '../hooks/useBackButton';
import styles from './AdminCommunitiesTab.module.css';

const EMPTY_COMMUNITY = {
  name: '',
  description: '',
  slug: '',
  pricing: 'free',
  price: 0,
  isPrivate: false,
  emoji: '🏫',
  color: '#6c63ff'
};

export default function AdminCommunitiesTab({ onToast }) {
  const { token } = useAuth();
  const { t } = useLang();
  
  const [communities, setCommunities] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  // Filters & Sorting state
  const [filterPrivacy, setFilterPrivacy] = useState('all');
  const [filterPricing, setFilterPricing] = useState('all');
  const [sortKey, setSortKey] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Create/Edit modal state
  const [modal, setModal] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = new
  const [form, setForm] = useState(EMPTY_COMMUNITY);
  const [saving, setSaving] = useState(false);

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: '',
    cancelText: '',
    onConfirm: null,
    type: 'warning'
  });

  async function loadCommunities() {
    try {
      const res = await fetch(`${API}/api/communities`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setCommunities(await res.json());
      }
    } catch (e) {
      console.error(e);
      onToast(t('common.error') || 'Failed to load communities');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadCommunities();
  }, []); // eslint-disable-line

  function triggerDelete(id, name) {
    setConfirmDialog({
      isOpen: true,
      title: 'Permanently Delete Community',
      message: `Are you absolutely sure you want to delete "${name}"? This action will permanently delete all courses, lessons, posts, members, and progress linked to this community.`,
      confirmText: 'Delete',
      cancelText: t('common.cancel', 'Cancel'),
      type: 'danger',
      onConfirm: () => confirmDeleteCommunity(id)
    });
  }

  async function confirmDeleteCommunity(id) {
    try {
      const res = await fetch(`${API}/api/communities/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        onToast(t('common.success') || 'Community deleted successfully');
        await loadCommunities();
      } else {
        const errorData = await res.json();
        onToast(errorData.error || t('common.error'));
      }
    } catch (e) {
      console.error(e);
      onToast(t('common.error'));
    } finally {
      setConfirmDialog(prev => ({ ...prev, isOpen: false }));
    }
  }

  function openNew() {
    setForm(EMPTY_COMMUNITY);
    setEditingId(null);
    setModal(true);
  }

  function openEdit(c) {
    setForm({
      name: c.name || '',
      description: c.description || '',
      slug: c.slug || '',
      pricing: c.pricing || 'free',
      price: c.price || 0,
      isPrivate: !!c.isPrivate,
      emoji: c.emoji || '🏫',
      color: c.color || '#6c63ff'
    });
    setEditingId(c.id);
    setModal(true);
  }

  function closeModal() {
    setModal(false);
    setEditingId(null);
  }

  useBackButton(closeModal, modal);

  async function saveCommunity(e) {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingId) {
        const res = await fetch(`${API}/api/communities/${editingId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            name: form.name,
            description: form.description,
            isPrivate: form.isPrivate
          })
        });

        if (res.ok) {
          onToast(t('common.success') || 'Community updated successfully');
          closeModal();
          await loadCommunities();
        } else {
          const errorData = await res.json();
          onToast(errorData.error || t('common.error'));
        }
      } else {
        const res = await fetch(`${API}/api/communities`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(form)
        });

        if (res.ok) {
          onToast(t('common.success') || 'Community created successfully');
          closeModal();
          await loadCommunities();
        } else {
          const errorData = await res.json();
          onToast(errorData.error || t('common.error'));
        }
      }
    } catch (e) {
      console.error(e);
      onToast(t('common.error'));
    } finally {
      setSaving(false);
    }
  }

  function handleSort(key) {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setCurrentPage(1);
  }

  // Filter logic
  const filtered = communities.filter(c => {
    const matchesSearch = !search ||
      c.name?.toLowerCase().includes(search.toLowerCase()) ||
      c.slug?.toLowerCase().includes(search.toLowerCase());
      
    const matchesPrivacy = filterPrivacy === 'all' || 
      (filterPrivacy === 'private' ? c.isPrivate === true : c.isPrivate === false);
      
    const matchesPricing = filterPricing === 'all' || 
      (filterPricing === 'free' ? c.pricing === 'free' : c.pricing !== 'free');

    return matchesSearch && matchesPrivacy && matchesPricing;
  });

  // Sort logic
  const sorted = [...filtered].sort((a, b) => {
    let aVal, bVal;
    
    switch (sortKey) {
      case 'name':
        aVal = (a.name || '').toLowerCase();
        bVal = (b.name || '').toLowerCase();
        break;
      case 'slug':
        aVal = (a.slug || '').toLowerCase();
        bVal = (b.slug || '').toLowerCase();
        break;
      case 'member_count':
        aVal = a.member_count || 0;
        bVal = b.member_count || 0;
        break;
      case 'online_count':
        aVal = a.online_count || 0;
        bVal = b.online_count || 0;
        break;
      case 'price':
        aVal = a.price || 0;
        bVal = b.price || 0;
        break;
      case 'created_at':
      default:
        aVal = new Date(a.created_at || a.createdAt || 0).getTime();
        bVal = new Date(b.created_at || b.createdAt || 0).getTime();
        break;
    }

    if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  // Paginate logic
  const paginated = sorted.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  function renderSortIndicator(key) {
    if (sortKey !== key) return null;
    return sortDir === 'asc' ? ' ▲' : ' ▼';
  }

  return (
    <div className="flex-col">
      <div className="card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div className="card-title">🏫 Community Portals</div>
          <button 
            className="btn btn-primary btn-sm" 
            onClick={openNew}
          >
            + Create Community
          </button>
        </div>
        <div className="card-body" style={{ paddingBottom: 16 }}>
          <div className={styles.searchBar}>
            <input 
              value={search} 
              onChange={e => { setSearch(e.target.value); setCurrentPage(1); }} 
              placeholder="Search by community name or slug..." 
              className={styles.searchInput}
            />
            <button className="btn btn-ghost btn-sm" onClick={loadCommunities}>🔄 Refresh</button>
          </div>
          
          <div className={styles.filterBar}>
            <select
              className={styles.filterSelect}
              value={filterPrivacy}
              onChange={e => { setFilterPrivacy(e.target.value); setCurrentPage(1); }}
            >
              <option value="all">All Privacy Models</option>
              <option value="public">Public</option>
              <option value="private">Private</option>
            </select>

            <select
              className={styles.filterSelect}
              value={filterPricing}
              onChange={e => { setFilterPricing(e.target.value); setCurrentPage(1); }}
            >
              <option value="all">All Pricing Types</option>
              <option value="free">Free Access</option>
              <option value="paid">Premium / Subscription</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text2)' }}>{t('common.loading')}</div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={styles.sortHeader} onClick={() => handleSort('name')}>
                      Community{renderSortIndicator('name')}
                    </th>
                    <th className={styles.sortHeader} onClick={() => handleSort('slug')}>
                      Slug Identifier{renderSortIndicator('slug')}
                    </th>
                    <th>Owner</th>
                    <th className={styles.sortHeader} onClick={() => handleSort('member_count')}>
                      Members Count{renderSortIndicator('member_count')}
                    </th>
                    <th className={styles.sortHeader} onClick={() => handleSort('online_count')}>
                      Online Active{renderSortIndicator('online_count')}
                    </th>
                    <th>Privacy Type</th>
                    <th className={styles.sortHeader} onClick={() => handleSort('price')}>
                      Pricing{renderSortIndicator('price')}
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map(c => (
                    <tr key={c.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span style={{ fontSize: 20 }}>{c.emoji || '🏫'}</span>
                          <div>
                            <div style={{ fontWeight: 600, color: 'var(--text)' }}>{c.name}</div>
                            <div className="text-muted" style={{ fontSize: 11 }} title={c.description || 'No description provided'}>
                              {c.description 
                                ? (c.description.length > 75 ? c.description.substring(0, 72) + '...' : c.description) 
                                : 'No description provided'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span style={{ fontFamily: 'monospace', background: 'var(--surface2)', padding: '2px 6px', borderRadius: 4, fontSize: 11 }}>
                          /{c.slug}
                        </span>
                      </td>
                      <td>
                        <div style={{ fontWeight: 500 }}>{c.owner_name || 'System Admin'}</div>
                      </td>
                      <td>
                        <span className={styles.countBadge}>{c.member_count || 0}</span>
                      </td>
                      <td>
                        <span style={{ color: (c.online_count || 0) > 0 ? 'var(--accent3)' : 'var(--text3)', fontWeight: 600 }}>
                          ● {c.online_count || 0}
                        </span>
                      </td>
                      <td>
                        <span className={`${styles.statusBadge} ${c.isPrivate ? styles.private : styles.public}`}>
                          <span className={styles.statusDot} />
                          {c.isPrivate ? 'Private' : 'Public'}
                        </span>
                      </td>
                      <td>
                        <span style={{ fontWeight: 600, color: c.pricing === 'free' ? 'var(--text3)' : 'var(--accent3)' }}>
                          {c.pricing === 'free' ? 'Free' : `฿${(c.price || 0).toLocaleString()}`}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn btn-ghost btn-sm" onClick={() => openEdit(c)}>✏️ Edit</button>
                          <button className="btn btn-danger btn-sm" onClick={() => triggerDelete(c.id, c.name)}>🗑️</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {paginated.length === 0 && (
                    <tr>
                      <td colSpan={8} style={{ textAlign: 'center', padding: 24, color: 'var(--text3)' }}>
                        No communities found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <Paginator
              totalItems={sorted.length}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={setItemsPerPage}
            />
          </>
        )}
      </div>

      {/* Community Creation/Edit Modal */}
      {modal && (
        <div className={styles.modalOverlay} onClick={e => e.target === e.currentTarget && closeModal()}>
          <div className={styles.modal} style={{ width: 'min(500px, 100%)' }}>
            <div className={styles.modalHeader}>
              <div className={styles.modalTitle}>{editingId ? 'Edit Community' : 'Create Community'}</div>
              <button className={styles.modalClose} onClick={closeModal}>×</button>
            </div>
            <div className={styles.modalBody}>
              <form onSubmit={saveCommunity}>
                <div className="form-group" style={{ marginBottom: 16 }}>
                  <label className="form-label">Community Name</label>
                  <input 
                    className="form-input" 
                    required 
                    value={form.name} 
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))} 
                  />
                </div>

                <div className="form-group" style={{ marginBottom: 16 }}>
                  <label className="form-label">Description</label>
                  <textarea 
                    className="form-input" 
                    style={{ minHeight: 70 }}
                    value={form.description} 
                    onChange={e => setForm(f => ({ ...f, description: e.target.value }))} 
                  />
                </div>

                {!editingId && (
                  <>
                    <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                      <div className="form-group">
                        <label className="form-label">Slug URL Path</label>
                        <input 
                          className="form-input" 
                          placeholder="e.g. general-learning"
                          value={form.slug} 
                          onChange={e => setForm(f => ({ ...f, slug: e.target.value }))} 
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Display Emoji</label>
                        <input 
                          className="form-input" 
                          value={form.emoji} 
                          onChange={e => setForm(f => ({ ...f, emoji: e.target.value }))} 
                        />
                      </div>
                    </div>

                    <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                      <div className="form-group">
                        <label className="form-label">Pricing Type</label>
                        <select 
                          className="form-input"
                          value={form.pricing}
                          onChange={e => setForm(f => ({ ...f, pricing: e.target.value }))}
                        >
                          <option value="free">Free Access</option>
                          <option value="one_time">One-time Payment</option>
                          <option value="subscription">Subscription Plan</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Access Price (฿)</label>
                        <input 
                          type="number"
                          min="0"
                          className="form-input" 
                          value={form.price} 
                          onChange={e => setForm(f => ({ ...f, price: Number(e.target.value) }))} 
                        />
                      </div>
                    </div>
                  </>
                )}

                <div className="form-group" style={{ marginBottom: 20 }}>
                  <label className="form-label">Privacy Model</label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, cursor: 'pointer' }}>
                    <input 
                      type="checkbox" 
                      checked={form.isPrivate} 
                      onChange={e => setForm(f => ({ ...f, isPrivate: e.target.checked }))} 
                      style={{ width: 18, height: 18 }} 
                    />
                    <span style={{ fontSize: 13, color: 'var(--text2)' }}>Make this a private community (requires invite)</span>
                  </label>
                </div>

                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                  <button type="button" className="btn btn-ghost" onClick={closeModal}>{t('common.cancel')}</button>
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? t('common.loading') : editingId ? 'Save Changes' : 'Create Community'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        cancelText={confirmDialog.cancelText}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        type={confirmDialog.type}
      />
    </div>
  );
}
